import type { DeviceConfiguration } from '@packages/types/device/lite/deviceConfiguration';
import { SensorType } from '@packages/types/device/lite/sensors';
import { GasConcUnit } from '@packages/types/device/common/gas_conc_unit';
import { getSensorConstraints } from '@packages/types/device/lite/lite_constraints';

type SensorOption = { value: number; label: string };

export const LITE_SENSOR_ARRAY_FIELDS = [
  'calibration_gas_conc',
  'calibration_interval',
  'bump_interval',
  'low_alarm',
  'high_alarm',
  'twa_alarm',
  'stel_alarm',
  'twa_period',
  'stel_period',
  'sensor_disabled',
  'auto_fresh_air_cal',
  'bump_test_threshold',
  'custom_lel',
  'lel_CF_Index',
  'vol_for_50_lel_ch4',
  'gas_conc_unit',
] as const;

const ARRAY_FIELD_DEFAULTS: Record<string, unknown> = {
  lel_CF_Index: { value: 0, label: 'Methane(1.0)' },
  vol_for_50_lel_ch4: { value: 0, label: '2.5%VOL(US)' },
  bump_test_threshold: 40,
  sensor_disabled: false,
  auto_fresh_air_cal: false,
  gas_conc_unit: { value: 0, label: 'PPM' },
  custom_lel: 1,
};

const OBJECT_FIELDS = new Set(['lel_CF_Index', 'vol_for_50_lel_ch4', 'gas_conc_unit']);

function getArrayValueAt(arr: unknown, index: number, fallback?: unknown): unknown {
  if (Array.isArray(arr)) return arr[index] ?? fallback;
  return arr ?? fallback;
}

/**
 * LITE 배열형 config에서 특정 인덱스의 센서 데이터를 평탄화한다.
 * 렌더링(extractLiteSensorData)과 검증(useConfigValidation) 양쪽에서 공유하는 단일 구현.
 * 어떤 센서 옵션(type/name)을 쓸지는 호출부에서 결정해 `sensor`로 넘긴다.
 */
export function buildLiteSensorData(
  config: Record<string, unknown>,
  sensor: SensorOption,
  index: number
): Record<string, unknown> {
  return {
    index,
    name: sensor.label,
    type: sensor,
    gas_conc_unit: getArrayValueAt(config.gas_conc_unit, index, ARRAY_FIELD_DEFAULTS.gas_conc_unit),
    calibration_gas_conc: getArrayValueAt(config.calibration_gas_conc, index),
    calibration_interval: getArrayValueAt(config.calibration_interval, index),
    bump_interval: getArrayValueAt(config.bump_interval, index),
    low_alarm: getArrayValueAt(config.low_alarm, index),
    high_alarm: getArrayValueAt(config.high_alarm, index),
    twa_alarm: getArrayValueAt(config.twa_alarm, index),
    stel_alarm: getArrayValueAt(config.stel_alarm, index),
    twa_period: getArrayValueAt(config.twa_period, index),
    stel_period: getArrayValueAt(config.stel_period, index),
    sensor_disabled: getArrayValueAt(config.sensor_disabled, index, false),
    auto_fresh_air_cal: getArrayValueAt(config.auto_fresh_air_cal, index, false),
    bump_test_threshold: getArrayValueAt(config.bump_test_threshold, index),
    custom_lel: getArrayValueAt(config.custom_lel, index),
    lel_CF_Index: getArrayValueAt(config.lel_CF_Index, index, ARRAY_FIELD_DEFAULTS.lel_CF_Index),
    vol_for_50_lel_ch4: getArrayValueAt(
      config.vol_for_50_lel_ch4,
      index,
      ARRAY_FIELD_DEFAULTS.vol_for_50_lel_ch4
    ),
  };
}

/**
 * LITE DeviceConfiguration에서 특정 인덱스의 센서 데이터를 추출한다 (렌더링용).
 * 센서 옵션은 config 우선, 없으면 장착 센서를 사용한다.
 */
export function extractLiteSensorData(
  config: Record<string, unknown>,
  equippedSensor: SensorOption,
  configSensor: SensorOption | undefined,
  index: number
): Record<string, unknown> {
  return buildLiteSensorData(config, configSensor || equippedSensor, index);
}

/**
 * 센서 카드의 변경 사항을 LITE DeviceConfiguration 배열 필드에 병합한다.
 * updated의 각 key를 해당 index 위치에 삽입.
 */
export function buildLiteSensorUpdate(
  prev: DeviceConfiguration,
  index: number,
  updated: Record<string, unknown>
): DeviceConfiguration {
  const config = { ...prev } as Record<string, unknown>;
  const arrayFieldSet = new Set<string>(LITE_SENSOR_ARRAY_FIELDS);

  for (const key of Object.keys(updated)) {
    if (arrayFieldSet.has(key)) {
      const currentArray = (config[key] as unknown[] | undefined) || [];
      const array = [...currentArray];

      while (array.length <= index) {
        array.push(ARRAY_FIELD_DEFAULTS[key] ?? 0);
      }

      const value = updated[key];
      if (OBJECT_FIELDS.has(key)) {
        if (typeof value === 'object' && value !== null && 'value' in value && 'label' in value) {
          array[index] = value;
        } else if (typeof value === 'number') {
          array[index] = { value, label: '' };
        } else {
          array[index] = value;
        }
      } else {
        array[index] = value;
      }

      config[key] = array;
    } else {
      config[key] = updated[key];
    }
  }

  return config as unknown as DeviceConfiguration;
}

/**
 * LITE(Multigas)에서 "활성화된 센서 중 CO2 센서"가 있는지 판단한다.
 * 활성화 = 장착됨(type이 NO_SENSOR=255가 아님) + 비활성화(sensor_disabled) 아님.
 * CO2 = CO2_CRANBERRY(PPM) 또는 CO2_CRANBERRY_VOL(VOL).
 */
export function hasActiveLiteCo2Sensor(config: Record<string, unknown> | undefined): boolean {
  if (!config) return false;
  const sensorTypes = (config.sensor_type as SensorOption[] | undefined) || [];
  const disabled = (config.sensor_disabled as boolean[] | undefined) || [];
  return sensorTypes.some(
    (sensor, index) =>
      !!sensor &&
      (sensor.value === SensorType.CO2_CRANBERRY ||
        sensor.value === SensorType.CO2_CRANBERRY_VOL) &&
      disabled[index] !== true
  );
}

/**
 * CO2_CRANBERRY 단위 변환을 고려한 센서 제약 조건 조회.
 */
export function getLiteSensorConstraint(
  sensorType: number,
  gasConUnit?: SensorOption
): Record<string, unknown> {
  if (sensorType === SensorType.CO2_CRANBERRY) {
    const effectiveType =
      gasConUnit?.value === GasConcUnit.VOL
        ? SensorType.CO2_CRANBERRY_VOL
        : SensorType.CO2_CRANBERRY;
    return (getSensorConstraints(effectiveType) ||
      getSensorConstraints(SensorType.O2)) as Record<string, unknown>;
  }
  return (getSensorConstraints(sensorType as SensorType) ||
    getSensorConstraints(SensorType.O2)) as Record<string, unknown>;
}

/**
 * CO2_CRANBERRY 단위별 소수점 자릿수 오버라이드를 포함한 decimal point 결정.
 */
export function getLiteSensorDecimalPoint(
  sensorValue: number,
  deviceDecimalPoints: number[] | undefined,
  index: number,
  gasConUnit?: SensorOption
): number | undefined {
  if (sensorValue === SensorType.CO2_CRANBERRY) {
    return gasConUnit?.value === GasConcUnit.VOL ? 2 : 0;
  }
  return deviceDecimalPoints?.[index];
}
